import React from 'react';
import { motion } from 'framer-motion';
import { ArrowLeftIcon, ArrowRightIcon, ArrowDownIcon } from 'lucide-react';
export function HcpWorkflowSlide({
  onNext,
  onBack
}: {
  onNext: () => void;
  onBack: () => void;
}) {
  return <div className="flex flex-col h-full px-6 py-8 relative z-10 bg-slate-950 text-slate-300" data-id="element-331">
      <div className="flex items-center justify-between mb-6" data-id="element-332">
        <button onClick={onBack} className="p-2 -ml-2 text-slate-400 hover:text-white transition-colors bg-slate-900 rounded-full shadow-sm border border-slate-800" aria-label="Go back" data-id="element-333">
          <ArrowLeftIcon className="w-5 h-5" data-id="element-334" />
        </button>
      </div>

      <div className="flex-1 flex flex-col max-w-sm mx-auto w-full overflow-y-auto no-scrollbar pb-24" data-id="element-335">
        <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} className="mb-8" data-id="element-336">
          <h2 className="text-2xl font-semibold text-white leading-tight mb-2" data-id="element-337">
            From referral review to clinic preparation
          </h2>
        </motion.div>

        {/* Side by side workflow comparison */}
        <div className="grid grid-cols-2 gap-4 mb-8" data-id="element-338">
          {/* Current */}
          <motion.div initial={{
          opacity: 0,
          x: -20
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          delay: 0.2
        }} className="flex flex-col items-center" data-id="element-339">
            <h3 className="text-slate-500 text-xs font-bold uppercase tracking-wider mb-4 text-center" data-id="element-340">
              Current workflow
            </h3>

            <div className="w-full space-y-2 flex flex-col items-center" data-id="element-341">
              <div className="bg-slate-900 border border-slate-800 text-slate-400 px-3 py-2 rounded-lg text-[11px] font-medium w-full text-center" data-id="element-342">
                Referral
              </div>
              <ArrowDownIcon className="w-3 h-3 text-slate-700" data-id="element-343" />
              <div className="bg-slate-800 border border-slate-700 text-slate-300 px-3 py-3 rounded-lg text-[11px] font-medium w-full text-center shadow-inner" data-id="element-344">
                Consultant review
              </div>
              <ArrowDownIcon className="w-3 h-3 text-slate-700" data-id="element-345" />
              <div className="bg-slate-900 border border-slate-800 text-slate-400 px-3 py-2 rounded-lg text-[11px] font-medium w-full text-center" data-id="element-346">
                Clinic allocation
              </div>
              <ArrowDownIcon className="w-3 h-3 text-slate-700" data-id="element-347" />
              <div className="bg-slate-900 border border-slate-800 text-slate-400 px-3 py-2 rounded-lg text-[11px] font-medium w-full text-center" data-id="element-348">
                Patient visit
              </div>
            </div>
          </motion.div>

          {/* Structured */}
          <motion.div initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          delay: 0.4
        }} className="flex flex-col items-center" data-id="element-349">
            <h3 className="text-nhs-lightblue text-xs font-bold uppercase tracking-wider mb-4 text-center" data-id="element-350">
              Structured workflow
            </h3>

            <div className="w-full space-y-2 flex flex-col items-center" data-id="element-351">
              <div className="bg-slate-900 border border-slate-800 text-slate-400 px-3 py-2 rounded-lg text-[11px] font-medium w-full text-center" data-id="element-352">
                Referral
              </div>
              <ArrowDownIcon className="w-3 h-3 text-slate-700" data-id="element-353" />
              <div className="bg-nhs-blue/20 border border-nhs-blue/30 text-nhs-lightblue px-3 py-2 rounded-lg text-[11px] font-medium w-full text-center" data-id="element-354">
                Patient intake
              </div>
              <ArrowDownIcon className="w-3 h-3 text-nhs-blue/50" data-id="element-355" />
              <div className="bg-nhs-blue/20 border border-nhs-blue/30 text-nhs-lightblue px-3 py-2 rounded-lg text-[11px] font-medium w-full text-center" data-id="element-356">
                Automatic grouping
              </div>
              <ArrowDownIcon className="w-3 h-3 text-nhs-blue/50" data-id="element-357" />
              <div className="bg-nhs-blue text-white px-3 py-2 rounded-lg text-[11px] font-bold w-full text-center shadow-lg shadow-nhs-blue/20" data-id="element-358">
                Consultant confirmation
              </div>
              <ArrowDownIcon className="w-3 h-3 text-slate-700" data-id="element-359" />
              <div className="bg-slate-900 border border-slate-800 text-slate-400 px-3 py-2 rounded-lg text-[11px] font-medium w-full text-center" data-id="element-360">
                Patient visit
              </div>
            </div>
          </motion.div>
        </div>

        {/* Metrics */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.6
      }} className="space-y-3 mb-6" data-id="element-361">
          <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 flex justify-between items-center" data-id="element-362">
            <span className="text-slate-400 text-sm" data-id="element-363">
              Consultant review time saved
            </span>
            <span className="text-nhs-lightblue font-bold" data-id="element-364">
              ≈ 3–4 hrs / week
            </span>
          </div>
          <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 flex justify-between items-center" data-id="element-365">
            <span className="text-slate-400 text-sm" data-id="element-366">Clinic preparation</span>
            <span className="text-white font-medium text-sm text-right" data-id="element-367">
              Same-day structured overview
            </span>
          </div>
        </motion.div>

        <motion.p initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 0.8
      }} className="text-xs text-slate-500 text-center leading-relaxed" data-id="element-368">
          The goal is not automated triage.
          <br data-id="element-369" />
          The goal is faster, better-informed clinic allocation.
        </motion.p>
      </div>

      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 1
    }} className="absolute bottom-6 left-6 right-6" data-id="element-370">
        <button onClick={onNext} className="w-full py-4 bg-nhs-blue text-white rounded-xl font-semibold text-lg flex items-center justify-center hover:bg-nhs-darkblue transition-colors shadow-lg shadow-nhs-blue/20 active:scale-[0.98] group" data-id="element-371">
          View example patients
          <ArrowRightIcon className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" data-id="element-372" />
        </button>
      </motion.div>
    </div>;
}